/**
 * CSC "Back to [Collection]" — preserves collection context across one navigation hop.
 *
 * Behavior:
 *  1. On a /collections/<handle> page (not a product within a collection):
 *     capture { url, title, ts } into sessionStorage.
 *  2. On a /products/<handle> page: if a fresh context exists (within TTL),
 *     prepend a "← Back to <title>" link to the PDP breadcrumb bar.
 *
 * Falls back gracefully:
 *  - No sessionStorage → no link rendered
 *  - No breadcrumb bar found → no link rendered (PDP keeps its hardcoded crumb)
 *  - Context older than TTL → cleared + no link
 *
 * Layout: scoped class `.csc-back-link` styled in a small block of inline CSS
 * injected once on first use.
 */
(function () {
  var KEY = 'csc_collection_context';
  var TTL_MS = 30 * 60 * 1000; // 30 minutes

  function nowOnCollectionPage() {
    var p = location.pathname;
    return p.indexOf('/collections/') === 0 && p.indexOf('/products/') === -1;
  }

  function nowOnProductPage() {
    return location.pathname.indexOf('/products/') === 0;
  }

  function readContext() {
    try {
      var raw = sessionStorage.getItem(KEY);
      if (!raw) return null;
      var ctx = JSON.parse(raw);
      if (!ctx || !ctx.url || !ctx.title || !ctx.ts) return null;
      if (Date.now() - ctx.ts > TTL_MS) {
        sessionStorage.removeItem(KEY);
        return null;
      }
      return ctx;
    } catch (_) {
      return null;
    }
  }

  function writeContext(ctx) {
    try { sessionStorage.setItem(KEY, JSON.stringify(ctx)); } catch (_) {}
  }

  function getCollectionTitleFromDom() {
    // Prefer the collection banner H1, then any main H1, then <title>.
    var h1 = document.querySelector('.collection-hero h1, main h1, .main-collection-banner__heading, .collection h1');
    if (h1) {
      var t = (h1.textContent || '').trim();
      if (t) return t;
    }
    // Fallback: parse document.title, stripping the shop suffix.
    var t = (document.title || '').split('|')[0].split('–')[0].split('—')[0].trim();
    return t || 'Collection';
  }

  function captureIfCollectionPage() {
    if (!nowOnCollectionPage()) return;
    // Strip any query string + trailing slash for the canonical URL.
    var url = location.pathname.replace(/\/$/, '');
    writeContext({
      url: url,
      title: getCollectionTitleFromDom(),
      ts: Date.now()
    });
  }

  function findBreadcrumbTarget() {
    // 1. Custom PDP breadcrumb bars (e.g., .npdp-breadcrumb-bar, .vpdp2-breadcrumb-bar)
    var bar = document.querySelector('[class*="breadcrumb-bar"]');
    if (bar) return bar;
    // 2. Dawn-default breadcrumb if it ever lands in this theme
    var nav = document.querySelector('nav.breadcrumb, .breadcrumbs, .breadcrumb');
    if (nav) return nav;
    // 3. Any element with id ending in "-top" (custom PDP wrappers)
    var wrapper = document.querySelector('[id$="-top"][class*="-pdp"]');
    if (wrapper) return wrapper;
    return null;
  }

  function injectStyles() {
    if (document.getElementById('csc-back-link-styles')) return;
    var s = document.createElement('style');
    s.id = 'csc-back-link-styles';
    s.textContent = [
      '.csc-back-link{',
        'display:inline-flex;align-items:center;gap:6px;',
        'font-family:var(--mono, "JetBrains Mono", monospace);',
        'font-size:11px;letter-spacing:0.18em;text-transform:uppercase;',
        'color:inherit;text-decoration:none;border-bottom:1px solid transparent;',
        'transition:color 0.18s, border-color 0.18s;',
        'padding-right:14px;margin-right:10px;',
        'border-right:1px solid currentColor;',
      '}',
      '.csc-back-link:hover{color:var(--csc-red, #c8102e);border-bottom-color:currentColor;}',
      '.csc-back-link__arrow{display:inline-block;transition:transform 0.18s;}',
      '.csc-back-link:hover .csc-back-link__arrow{transform:translateX(-2px);}',
      '/* Tighten on mobile so the existing breadcrumb still fits */',
      '@media (max-width: 540px){',
        '.csc-back-link{font-size:10px;letter-spacing:0.12em;padding-right:10px;margin-right:8px;}',
      '}'
    ].join('');
    document.head.appendChild(s);
  }

  function renderBackLinkIfApplicable() {
    if (!nowOnProductPage()) return;
    var ctx = readContext();
    if (!ctx) return;
    if (document.querySelector('.csc-back-link')) return; // idempotent
    var target = findBreadcrumbTarget();
    if (!target) return;

    injectStyles();

    var a = document.createElement('a');
    a.className = 'csc-back-link';
    a.href = ctx.url;
    a.setAttribute('aria-label', 'Back to ' + ctx.title);
    a.innerHTML = '<span class="csc-back-link__arrow" aria-hidden="true">←</span> Back to ' + escapeHtml(ctx.title);

    // Insertion strategy:
    //   - If target is a breadcrumb bar with a child crumb, insert as first child
    //     of the inner element that holds the existing crumbs.
    //   - Otherwise prepend directly to the target.
    var inner = target.querySelector(':scope > div, :scope > nav');
    (inner || target).insertBefore(a, (inner || target).firstChild);
  }

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;' }[c];
    });
  }

  function init() {
    captureIfCollectionPage();
    renderBackLinkIfApplicable();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
